import React from 'react';
import ReactDOM from 'react-dom';
import App, { getStore } from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { ProfileDropdown } from './components/common/NewHeaderComponent';
import { getMe, getSingleDoctor, isSingleDoctorAccess } from './store/idbHandler';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import { his } from './pages/history';
import MHPopup from './components/common/MHPopup';
import { NewSwitchProfileDropdown } from './components/common/NewSwitchProfileDropdown';
import ClinicSelector from './components/common/ClinicSelector';
import { FamilyIcon } from './components/RelatedProfiles/FamilyIcon';

ReactDOM.render(
    <React.StrictMode>
        <App />
    </React.StrictMode>,
    document.getElementById('root'),
);

export interface RendererParams {
    containerId: string;
    [key: string]: string;
}

export interface RendererClinicSelectorParams {
    containerId: string;
    onClinicSelect: (selectedClinicId: string) => void;
    className?: string;
    showOnlyAbhaClinics?: boolean;
    currentClinicId?: string;
}

const containersCreated: Set<string> = new Set();

export function renderFamilyIcon(inputParams: RendererParams) {
    const { containerId, pid, key } = inputParams;
    const store = getStore();
    ReactDOM.render(
        <Provider store={store}>
            <Router history={his}>
                <FamilyIcon pid={pid} key={key} />
            </Router>
        </Provider>,
        document.getElementById(containerId),
    );
    addUnMountEventListener({ containerId });
}

function renderProfileButton(inputParams: RendererParams) {
    const { containerId } = inputParams;
    const me = getMe();
    const doc = getSingleDoctor();

    let dName = `${(doc
        ? [doc?.profile.personal.name.f, doc?.profile.personal.name.l]
        : me?.docs?.[0] && isSingleDoctorAccess()
        ? [me?.docs?.[0].personal.name.f, me?.docs?.[0].personal.name.l]
        : []
    )
        .filter(Boolean)
        .join(' ')}`.trim();

    if (dName.trim()) {
        dName = `Dr. ${dName}`;
    }

    if (!dName && me) {
        dName = `${me.user.fn}`;
    }

    const imgUrl =
        doc?.profile.personal.pic ||
        (isSingleDoctorAccess() && me?.docs?.[0]?.personal?.pic
            ? me?.docs?.[0]?.personal?.pic
            : `https://a.eka.care/user-avatar/${me?.user.oid}`);

    const store = getStore();

    const inviteCode = doc?.profile.professional.invite?.invite_code;
    const inviteLink = doc?.profile.professional.invite?.invite_link;

    const showInviteCode = !!inviteCode;
    const showInviteLink = !!inviteLink;

    const message = `${dName} has invited you to eka.care. Use invite code ${doc?.profile.professional.invite?.invite_code} or click here ${doc?.profile.professional.invite?.invite_link} to consult with ${dName}. A connected healthcare experience awaits you on eka.care.`;
    // const history = useHistory();

    ReactDOM.render(
        <Provider store={store}>
            <Router history={his}>
                <ProfileDropdown
                    dName={dName}
                    imgUrl={imgUrl}
                    inviteCode={inviteCode}
                    showInviteCode={showInviteCode}
                    message={message}
                    showInviteLink={showInviteLink}
                    // history={history}
                />
            </Router>
        </Provider>,
        document.getElementById(containerId),
    );

    addUnMountEventListener({ containerId });
}

function renderSwitchProfileButton(inputParams: RendererParams) {
    const { containerId } = inputParams;

    const store = getStore();

    ReactDOM.render(
        <Provider store={store}>
            <Router history={his}>
                <NewSwitchProfileDropdown />
            </Router>
        </Provider>,
        document.getElementById(containerId),
    );

    addUnMountEventListener({ containerId });
    return;
}

function renderMHPopup(inputParams: { containerId: string; ptOid: string }) {
    const { ptOid, containerId } = inputParams;
    const store = getStore();

    ReactDOM.render(
        <Provider store={store}>
            <Router history={his}>
                <MHPopup patient_oid={ptOid} />
            </Router>
        </Provider>,
        document.getElementById(containerId),
    );

    addUnMountEventListener({ containerId });
}

function addUnMountEventListener({ containerId }: { containerId: string }) {
    if (!containersCreated.has(containerId)) {
        containersCreated.add(containerId);
        window.addEventListener('eka-event', destroyContainerEventListener as EventListener);
    }
}

function destroyContainerEventListener(
    e: CustomEvent<{ containerId: string; type: 'DESTROY_CONTAINER' }>,
) {
    console.log('In event listener', 'before check');
    const containerToDestory = e.detail.containerId;
    if (e.detail.type === 'DESTROY_CONTAINER' && containersCreated.has(containerToDestory)) {
        console.log('In event listener', 'check passed');
        const nodeOfMountedApp = document.getElementById(containerToDestory);
        if (!!nodeOfMountedApp) {
            console.log('Node found', 'unmounting node');
            containersCreated.delete(containerToDestory);
            ReactDOM.unmountComponentAtNode(nodeOfMountedApp);
            window.removeEventListener('eka-event', destroyContainerEventListener as EventListener);
        }
    }
}
function renderClinicSelector(inputParams: RendererClinicSelectorParams) {
    const {
        containerId,
        onClinicSelect,
        className,
        showOnlyAbhaClinics,
        currentClinicId,
    } = inputParams;

    const store = getStore();

    ReactDOM.render(
        <Provider store={store}>
            <Router history={his}>
                <ClinicSelector
                    onClinicSelect={onClinicSelect}
                    className={className}
                    showOnlyAbhaClinics={showOnlyAbhaClinics}
                    currClinicId={currentClinicId}
                />
            </Router>
        </Provider>,
        document.getElementById(containerId),
    );

    addUnMountEventListener({ containerId });
}

// Expose the function globally
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.renderClinicSelector = renderClinicSelector;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.renderProfileButton = renderProfileButton;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.renderFamilyIcon = renderFamilyIcon;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.renderMHPopup = renderMHPopup;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.renderSwitchProfileButton = renderSwitchProfileButton;

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
// serviceWorkerRegistration.register();
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
